import {
    Navigate, useLocation,
    useParams, Outlet, useResolvedPath, useNavigate,
} from 'react-router-dom'
import {
    useCallback, useEffect,
    useMemo, useState,
} from 'react'
import get from 'lodash/get'
import {
    RequestConfig,
} from 'app/types/request.types'
import useSkyNetRoutes from 'app/SkyNetRoutes/hooks/useSkyNetRoutes'
import {
    CommonRoutes,
} from 'app/enums/common.enums'
import SideBar from 'app/shared-components/SideBar'
import Tabs from 'app/shared-components/Tabs'
import useGetDomainObjectById from 'app/common/DomainObject/hooks/useGetDomainObjectById'
import useFirstLoading from 'app/hooks/useFirstLoading'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    SkyNetTableConfig,
    SkyNetTableTab,
    SkyNetTableTabWithLabel,
} from 'app/shared-components/SkyNetTable/types/skyNetTable.types'
import useTableTab from 'app/shared-components/SkyNetTable/hooks/useTableTab'

import {
    SidebarTab,
} from '../SkyNetDomain.types'
import useSkyNetDomainContext from '../useSkyNetDomainContext'

import useStyles from './SkyNetDomainTabs.styles'

const SkyNetDomainTabs = ({
    getDomainObject,
    tabs,
    tableConfig,
}: Readonly<{
    getDomainObject?: (params: Record<string, any>) => RequestConfig,
    tabs: SidebarTab[],
    tableConfig?: SkyNetTableConfig,
}>) => {
    const {
        classes,
    } = useStyles()
    const {
        setSelectedTab,
        selectedTab,
        requestKey,
    } = useSkyNetDomainContext()

    // Find the selected table tab, to check if the config has a custom set of
    // form tabs for this table tab
    // TODO-JP: duplicate from SkyNetTableContainer, should find a way to share it
    const tableTabs = useMemo((): SkyNetTableTabWithLabel[] => {
        const tableConfigtabs: SkyNetTableTab[] = get(tableConfig, 'tabs', [])

        return tableConfigtabs.map((tab) => {
            return {
                label: tab.name,
                ...tab,
            }
        })
    }, [tableConfig])

    const selectedTableTab = useTableTab({
        tabs: tableTabs,
    })

    const formTabs = useMemo(() => {
        const selectedTableTabIndex = tableTabs.findIndex(({
            url,
        }) => {
            return url === selectedTableTab?.url
        })

        if ((selectedTableTabIndex >= 0) && (tableTabs?.length > selectedTableTabIndex)) {
            if (tableTabs[selectedTableTabIndex].formTabs) {
                // eslint-disable-next-line prefer-destructuring
                return tableTabs[selectedTableTabIndex].formTabs
            }
        }
        return tabs
    }, [
        tableTabs,
        selectedTableTab,
        tabs,
    ])

    const {
        key, ...rest
    } = useParams()

    const navigate = useNavigate()
    const {
        pathname,
    } = useResolvedPath('..')
    const {
        state,
    } = useLocation()

    const {
        data,
        isDataReady,
        isError,
        error,
        isFetching,
    } = useGetDomainObjectById({
        id: key,
        requestKey,
        requestFn: getDomainObject,
    })

    const [
        sidebarTabs,
        setSidebarTabs,
    ] = useState<SidebarTab[]>(undefined)

    useEffect(() => {
        async function filterTabs(fTabs) {
            Promise.all(fTabs.map(async (tab) => {
                if (tab?.condition) {
                    const result = await tab.condition(data)

                    return {
                        ...tab, result,
                    }
                }

                return {
                    ...tab, result: true,
                }
            })).then((res) => {
                setSidebarTabs(res.filter(({
                    result,
                }) => {
                    return result
                }))
            })
        }

        if (!getDomainObject) {
            setSidebarTabs(formTabs)
            return
        }

        if (data) {
            filterTabs(formTabs)
        }
    }, [
        isDataReady,
        data,
        getDomainObject,
        formTabs,
    ])

    const isSideBarTabAvailable = useCallback((tab) => {
        return !!(sidebarTabs || []).find(({
            url,
        }) => {
            return tab?.split('/').includes(url)
        })
    }, [sidebarTabs])

    const loaded = useFirstLoading(!isFetching)

    const RedirectElement = useMemo(() => {
        const route = rest[CommonRoutes.Asterisk] || selectedTab

        if (!sidebarTabs) return null
        if (isSideBarTabAvailable(route)) {
            return (
                <Navigate
                    to={route}
                    state={state}
                    replace
                />
            )
        }
        return (
            <Navigate
                to={sidebarTabs?.[0]?.url || '..'}
                state={state}
                replace
            />
        )
    }, [
        rest,
        selectedTab,
        isSideBarTabAvailable,
        sidebarTabs,
        state,
    ])

    return useSkyNetRoutes({
        routes: [{
            path: `${CommonRoutes.Slash}`,
            element: (
                <StatusHandler
                    isSuccess={loaded || !getDomainObject}
                    isError={isError}
                    error={error}
                    isFetching={isFetching}
                >
                    <SideBar
                        onClose={() => {
                            navigate(pathname)
                        }}
                    >
                        <div className={classes.tabsWrapper}>
                            <div className={classes.tabs}>
                                <Tabs
                                    items={sidebarTabs}
                                    setSelectedTab={setSelectedTab}
                                />
                            </div>
                        </div>
                        <Outlet />
                        {RedirectElement}
                    </SideBar>
                </StatusHandler>
            ),
            children: [
                ...(sidebarTabs || []).map(({
                    url,
                    Component,
                    params,
                }) => {
                    return {
                        path: `${url}/${params?.length ? params?.concat(CommonRoutes.Slash) : ''}${CommonRoutes.Asterisk}`,
                        element: (
                            <>
                                <div className={classes.tabContentWrapper}>
                                    <div className={classes.tabContent}>
                                        <Component
                                            id={key}
                                            data={data}
                                        />
                                    </div>
                                </div>
                            </>
                        ),
                    }
                }),
                {
                    path: `${CommonRoutes.Slash}${CommonRoutes.Asterisk}`,
                    element: RedirectElement,
                },
            ],
        }],
    })
}

export default SkyNetDomainTabs
